import { useState, useEffect } from 'react';
import { Camera, Shield, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function JobGallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await axios.get(`${API}/api/gallery/public?limit=12`);
        setPhotos(res.data.photos || []);
      } catch (e) {
        // Silently fail - gallery is optional
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  // Placeholder photos when gallery is empty (show service-type stock images)
  const placeholders = [
    { image_url: 'https://images.unsplash.com/photo-1530046339160-ce3e530c7d2f?w=600&h=400&fit=crop', caption: 'Flat tire change', service_type: 'flat_tire' },
    { image_url: 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=600&h=400&fit=crop', caption: 'Engine diagnostics', service_type: 'diagnostic' },
    { image_url: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=600&h=400&fit=crop', caption: 'Jump start service', service_type: 'jump_start' },
    { image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=600&h=400&fit=crop', caption: 'Oil change', service_type: 'oil_change' },
    { image_url: 'https://images.unsplash.com/photo-1507136566006-cfc505b114fc?w=600&h=400&fit=crop', caption: 'Brake repair', service_type: 'brake_repair' },
    { image_url: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=600&h=400&fit=crop', caption: 'Towing service', service_type: 'tow' },
  ];

  const displayPhotos = photos.length > 0 ? photos : placeholders;
  const isPlaceholder = photos.length === 0;

  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % Math.max(1, displayPhotos.length - 2));
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + Math.max(1, displayPhotos.length - 2)) % Math.max(1, displayPhotos.length - 2));

  if (loading) return null;

  return (
    <section className="py-20 bg-white" data-testid="job-gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Camera className="w-5 h-5 text-[#1a56db]" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#1a56db]">Our Work</p>
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 mb-3">
            Real Jobs. Real Results.
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Every photo is from an actual completed service — AI-verified for privacy, no personal information shown.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Shield className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">AI-moderated for privacy protection</span>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayPhotos.slice(activeIndex, activeIndex + 6).map((photo, idx) => (
              <div 
                key={photo.photo_id || idx} 
                className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-100"
                data-testid={`gallery-photo-${idx}`}
              >
                <img
                  src={photo.image_url}
                  alt={photo.caption || 'Completed service'}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <p className="text-white text-sm font-medium">{photo.caption || photo.service_type?.replace(/_/g, ' ')}</p>
                  {photo.provider_name && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                      <span className="text-white/80 text-xs">by {photo.provider_name}</span>
                    </div>
                  )}
                </div>
                {isPlaceholder && (
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs text-gray-600 font-medium">
                    Sample
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {displayPhotos.length > 6 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                data-testid="gallery-next"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function CheckrBadge({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
    lg: 'px-4 py-2 text-sm gap-2',
  };

  return (
    <div className={`inline-flex items-center bg-[#1a56db]/10 border border-[#1a56db]/20 rounded-full ${sizes[size]} ${className}`} data-testid="checkr-badge">
      <Shield className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} text-[#1a56db]`} />
      <span className="font-semibold text-[#1a56db]">Checkr</span>
      <span className="text-gray-600">Verified</span>
    </div>
  );
}

export function CheckrTrustSection() {
  return (
    <section className="py-16 bg-gradient-to-r from-[#f0f4ff] to-white border-y border-blue-100" data-testid="checkr-trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#1a56db] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#1a56db]">Your Safety First</p>
                <h3 className="text-2xl font-bold text-gray-900">Background Checked Providers</h3>
              </div>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">
              Every service provider on Jiffys undergoes a comprehensive background check through 
              <strong className="text-[#1a56db]"> Checkr</strong>, the industry-leading background screening platform. 
              This includes criminal history, driving record, and identity verification so you can feel safe 
              letting our technicians work on your vehicle.
            </p>
            <div className="flex flex-wrap gap-3">
              {['Criminal Background', 'Driving Record', 'Identity Verified', 'Drug Screening'].map((item) => (
                <div key={item} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-blue-100 shadow-sm">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 text-center min-w-[240px]">
              <div className="w-16 h-16 bg-[#1a56db] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <p className="text-lg font-bold text-gray-900 mb-1">Powered by</p>
              <p className="text-2xl font-black text-[#1a56db] tracking-tight">Checkr</p>
              <p className="text-xs text-gray-500 mt-2">Background Check Platform</p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Trusted by 100K+ companies</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function DriverProfileCard({ driver, showRating = true, compact = false }) {
  if (!driver) return null;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${compact ? 'p-3' : 'p-5'}`} data-testid="driver-profile-card">
      <div className="flex items-center gap-4">
        {/* Profile Picture */}
        <div className={`relative flex-shrink-0 ${compact ? 'w-12 h-12' : 'w-16 h-16'}`}>
          {driver.picture ? (
            <img
              src={driver.picture.startsWith('/api') ? `${API}${driver.picture}` : driver.picture}
              alt={driver.name}
              className="w-full h-full rounded-full object-cover border-2 border-white shadow"
              data-testid="driver-avatar"
            />
          ) : (
            <div className={`w-full h-full rounded-full bg-[#FF3008] flex items-center justify-center`} data-testid="driver-avatar-placeholder">
              <span className="text-white font-bold text-lg">
                {driver.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-bold text-gray-900 truncate ${compact ? 'text-sm' : 'text-base'}`}>
            {driver.name || 'Your Technician'}
          </h4>
          {showRating && (
            <div className="flex items-center gap-1 mt-0.5">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-semibold text-gray-900">{driver.rating || '5.0'}</span>
              {driver.completed_jobs && (
                <span className="text-xs text-gray-500 ml-1">({driver.completed_jobs} jobs)</span>
              )}
            </div>
          )}
          {!compact && (
            <CheckrBadge size="sm" className="mt-2" />
          )}
        </div>
      </div>
    </div>
  );
}
